'use client'

import { usePathname } from 'next/navigation'
import { useIdentity } from '@/lib/providers/identity'
import { DEFAULT_PLATFORM_SCOPE } from '@/domain/scope/platform-scope'
import { firmScopeOf } from '@/domain/scope/request-scope'
import { UnderwriterShell } from './underwriter-shell'
import { cn } from '@/lib/utils'
import { SHELL_HEIGHT_CLASS } from '@/shared/ui/use-publish-banner-height'
import { MFA_SETUP_PATH } from '@/shared/lib/mfa-route-lock'

interface AppShellProps {
  children: React.ReactNode
}

// Shown while identity is loading to prevent layout flash.
function FullScreenSpinner() {
  return (
    // THE VIEWPORT MINUS THE BANNERS, the third of these and the same reason
    // as the other two (review round 2, MINOR C): a banner sits above this as
    // an in-flow block, so a whole viewport here is a second one and the page
    // scrolls by its height while identity loads.
    <div className={cn('flex items-center justify-center', SHELL_HEIGHT_CLASS)}>
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#5651ff] border-t-transparent" />
    </div>
  )
}

/**
 * The ROOT shell switch (`src/app/layout.tsx`), as narrowed by 002 H13.
 *
 * It used to be the only thing that put an underwriter under their own chrome,
 * on every route in the app. The Underwriter product now owns a route prefix
 * with its own layout, gate and scope provider, so this file's job shrank to
 * a few things:
 *
 *  0. `/security/mfa-setup` renders BARE for everyone: it is the enrolment
 *     gate, and a user who has not enrolled may see nothing else.
 *  1. `/underwriter/**` renders BARE — `src/app/underwriter/(app)/layout.tsx`
 *     supplies the chrome there. Wrapping it here too would mount two navbars
 *     and, worse, the shell's legacy redirect hook above the section's own gate.
 *  2. Everywhere else, an Underwriter-only identity keeps the legacy shell for
 *     the one release the old paths still resolve — including the PUBLIC
 *     directory page `/marketplace/underwriter/{slug}`, which is their own
 *     listing and is not moving.
 *
 * The switch reads the acting firm's SCOPE (`firmScopeOf`, 019 A5), not
 * `organizationType` and not a claimed slug (002 H13; the two old signals are
 * `underwriter-gate.ts`'s negative case).
 */
export function AppShell({ children }: AppShellProps) {
  const { identity, identityScope, isLoading } = useIdentity()
  const pathname = usePathname()

  // The Underwriter product's own prefix owns its chrome, gate and scope.
  if (pathname.startsWith('/underwriter')) return <>{children}</>

  // MFA enrolment is a gate, not a page of the app: it renders alone, with no
  // product chrome, so a user who has not enrolled sees nothing else and has
  // nowhere to click to. `MfaRouteGuard` keeps them here.
  if (pathname === MFA_SETUP_PATH) return <>{children}</>

  if (isLoading) return <FullScreenSpinner />

  // Unauthenticated — let downstream pages (sign-in, onboarding) handle rendering.
  if (!identity) return <>{children}</>

  // A brokerage firm ⇒ the broker surfaces answer for them; per-route layouts
  // supply the chrome.
  //
  // An identity resolved for ANOTHER product cannot answer this (R-1): while a
  // broker's `insurance-broker` read is in flight the provider may still be holding the
  // underwriter identity, whose scope would swap the whole app's chrome for
  // one render. Pass through until the core answer arrives.
  if (identityScope !== DEFAULT_PLATFORM_SCOPE || firmScopeOf(identity) === DEFAULT_PLATFORM_SCOPE)
    return <>{children}</>

  return <UnderwriterShell>{children}</UnderwriterShell>
}
